import { useEffect, useRef } from 'react'

/**
 * The keyboard layer for the review screens.
 *
 * Review is a keyboard screen: the fast path through a stack of scans is
 * Enter, Enter, correct, Enter, and a person who has to reach for the mouse
 * between fields does half the documents in an hour. So the bindings are a
 * first-class object rather than a pile of `onKeyDown` handlers — they are
 * *data*, and a screen renders its own help from the same list it binds, so
 * the keys can never quietly disagree with the legend that documents them.
 *
 * Three rules it will not break:
 *  - it yields to a focused input. Someone typing "Sonnenschein" into a
 *    correction must not trigger "nächstes Feld" on the n.
 *  - it never claims a chord the browser owns. Only chords a binding declares
 *    are consumed, and only then is `preventDefault` called.
 *  - it ignores keystrokes that are part of composing a character (IME, dead
 *    keys — a German keyboard makes ä with one).
 */

/** A chord, written like "Enter", "Mod+Enter", "Shift+ArrowDown". `Mod` is
 *  ⌘ on a Mac and Ctrl everywhere else, which is what people expect and what
 *  the design's "⌘Enter" means on the other platform. */
export type Chord = string

export interface KeyBinding {
  /** Stable id, for the help list and for tests. */
  id: string
  /** One chord or several that do the same thing. */
  keys: Chord | Chord[]
  /** German, imperative, ready to render: "Wert übernehmen". */
  label: string
  run: (event: KeyboardEvent) => void
  /**
   * Fire even while a text input has focus. False by default — this is the
   * flag that keeps the layer out of the way of typing. ⌘Enter sets it,
   * because a chord with a modifier cannot be part of a word.
   */
  whenTyping?: boolean
  /** A binding that exists but cannot act right now: still listed in the
   *  help (greyed by the screen), never fired. */
  disabled?: boolean
  /** Bound but not worth a line in the help. */
  hidden?: boolean
}

const IS_APPLE =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform ?? '')

/** Whether the event's target is somewhere a person is typing. */
export function isTypingTarget(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null
  if (!element || typeof element.tagName !== 'string') return false
  const tag = element.tagName.toLowerCase()
  if (tag === 'textarea' || tag === 'select') return true
  if (tag === 'input') {
    // A checkbox or a radio is not typing: Enter and the arrows are ours
    // there, which is what makes the binary editors keyboard-operable.
    const type = (element as HTMLInputElement).type
    return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range'].includes(type)
  }
  return element.isContentEditable === true
}

interface ParsedChord {
  key: string
  mod: boolean
  shift: boolean
  alt: boolean
}

function parseChord(chord: Chord): ParsedChord {
  const parts = chord.split('+').map((part) => part.trim())
  const key = parts.pop() ?? ''
  const lower = parts.map((part) => part.toLowerCase())
  return {
    key: key.toLowerCase(),
    mod: lower.includes('mod') || lower.includes('cmd') || lower.includes('meta') || lower.includes('ctrl'),
    shift: lower.includes('shift'),
    alt: lower.includes('alt') || lower.includes('option'),
  }
}

/** Whether one chord describes this event. Undeclared modifiers must be
 *  absent, so "Enter" does not also swallow ⌘Enter. */
export function matchesChord(chord: Chord, event: KeyboardEvent): boolean {
  const parsed = parseChord(chord)
  if (event.key.toLowerCase() !== parsed.key) return false
  const mod = event.metaKey || event.ctrlKey
  if (mod !== parsed.mod) return false
  if (event.shiftKey !== parsed.shift) return false
  if (event.altKey !== parsed.alt) return false
  return true
}

const SYMBOLS: Record<string, string> = {
  mod: IS_APPLE ? '⌘' : 'Strg',
  cmd: '⌘',
  meta: '⌘',
  ctrl: 'Strg',
  shift: '⇧',
  alt: IS_APPLE ? '⌥' : 'Alt',
  option: '⌥',
  enter: 'Enter',
  escape: 'Esc',
  arrowup: '↑',
  arrowdown: '↓',
  arrowleft: '←',
  arrowright: '→',
}

/** One chord as the keys to draw in `<kbd>` elements, in press order. */
export function chordSymbols(chord: Chord): string[] {
  return chord
    .split('+')
    .map((part) => part.trim())
    .map((part) => SYMBOLS[part.toLowerCase()] ?? part)
}

export interface KeyHelpEntry {
  id: string
  label: string
  /** Each alternative as its own list of keys. */
  chords: string[][]
  disabled: boolean
}

/** The bindings as a help list. Same array the layer binds, so a shortcut
 *  that exists is documented and one that is documented exists. */
export function keyHelp(bindings: KeyBinding[]): KeyHelpEntry[] {
  return bindings
    .filter((binding) => !binding.hidden)
    .map((binding) => ({
      id: binding.id,
      label: binding.label,
      chords: (Array.isArray(binding.keys) ? binding.keys : [binding.keys]).map(chordSymbols),
      disabled: binding.disabled === true,
    }))
}

/** Finds the binding an event should run, or undefined. Exported for tests
 *  and for a screen that wants to handle the event itself. */
export function bindingFor(bindings: KeyBinding[], event: KeyboardEvent): KeyBinding | undefined {
  const typing = isTypingTarget(event.target)
  return bindings.find((binding) => {
    if (binding.disabled) return false
    if (typing && !binding.whenTyping) return false
    const chords = Array.isArray(binding.keys) ? binding.keys : [binding.keys]
    return chords.some((chord) => matchesChord(chord, event))
  })
}

export interface KeysOptions {
  /** Off while a dialog is open, or while the queue is empty. */
  enabled?: boolean
  /** Where to listen. Defaults to the document, which is what a
   *  whole-screen shortcut layer wants. */
  target?: HTMLElement | Document | null
}

/**
 * Binds `bindings` for as long as the calling component is mounted.
 *
 * The bindings are read through a ref, so a screen may rebuild the array on
 * every render (it will — the handlers close over the focused field) without
 * re-subscribing the listener.
 */
export function useKeys(bindings: KeyBinding[], { enabled = true, target }: KeysOptions = {}) {
  const latest = useRef(bindings)
  latest.current = bindings

  useEffect(() => {
    if (!enabled) return
    const node: HTMLElement | Document = target ?? document

    function handle(event: Event) {
      const keyEvent = event as KeyboardEvent
      // Someone else already acted on this key; and a keystroke that is
      // still assembling a character is not a shortcut.
      if (keyEvent.defaultPrevented || keyEvent.isComposing || keyEvent.keyCode === 229) return
      const binding = bindingFor(latest.current, keyEvent)
      if (!binding) return
      keyEvent.preventDefault()
      binding.run(keyEvent)
    }

    node.addEventListener('keydown', handle)
    return () => node.removeEventListener('keydown', handle)
  }, [enabled, target])
}

export interface KeysProps extends KeysOptions {
  bindings: KeyBinding[]
}

/** The layer as an element, so a screen can drop it into its JSX where the
 *  keys belong and keep the same array for its help panel. Renders nothing. */
export function Keys({ bindings, ...options }: KeysProps) {
  useKeys(bindings, options)
  return null
}
